import {
  TableActions,
  TableActionTypes,
  EditRow,
  DownloadRow,
  ViewRow,
  RestoreRow,
  ArchiveRow,
  DeleteRow
} from './table.actions';
import { all } from 'deepmerge';
import { getTableData } from './table.selectors';
import { getRowId } from './utilities';
import {
  FirestoreTableState,
  TableState,
  firestoreTableInitialState,
  DxTableState
} from './table-definition';
import {
  DxDocument,
  IWithTableRowMeta,
  ActionButtonDefinition,
  ActionButtonType
} from '@digitaix/types';

export const DEFAULT_TABLE_STATE = {
  sorting: {
    __enabled: true,
    __updatedAt: {
      enabled: true,
      direction: 0
    },
    __createdAt: {
      enabled: true,
      direction: 1
    }
  },
  pagination: {
    enabled: true,
    size: 10,
    page: 0,
    pageSizeOptions: [5, 10, 25, 100]
  },
  filtering: { __enabled: true },
  expandable: {
    row: null,
    edit: false,
    full: true,
    btn: false
  },
  currentTab: null,
  importTableData: false,
  exportTableData: false,
  limit: 100,
  downloadRow: false,
  getCollectionPath: null,
  viewComponentId: null,
  selectedRows: {},
  tableSettings: {
    enabled: true,
    dialogEdit: true,
    dialogView: true,
    archived: true,
    createdAt: true,
    updatedAt: true
  },
  tabs: [],
  editRow: true,
  deleteMessage:
    '<p>Soll der Datensatz <b>unwiderruflich</b> gelÃ¶scht werden?</p>',
  expandRow: true,
  deleteRow: true,
  addRow: true,
  archiveRow: true,
  sortable: false,
  restoreRow: true,
  manuelArchive: false,
  manuelDelete: false,
  load: [],
  categoriesCollectionPath: null,
  selectableRows: false,
  viewRow: true,
  title: null,
  subtitle: null,
  query: null,
  showArchived: false,
  showCreatedAt: false,
  showUpdatedAt: false,
  dialogEdit: true,
  dialogView: false,
  dialogAdd: true,
  expandEdit: false,
  expandView: true,
  formId: null,
  editFormId: null,
  createFormId: null,
  hiddenColumns: [],
  getTableData: getTableData,
  columns: [],
  displayedColumns: [],
  viewComponent: null
} as TableState<any>;

export function getTableActionsFromRegister(
  tableRegister: Partial<TableState<any>>
): ActionButtonDefinition<any>[] {
  const actions: any[] = [];

  if (tableRegister.archiveRow) {
    actions.push({
      name: 'archive',
      action: (row, tableId, index, definition, collectionPath) =>
        new ArchiveRow(tableId, row, collectionPath),
      svg: true,
      disabled: (row: IWithTableRowMeta) => row.__archive === false,
      hide: (row: DxDocument) => row.__archived,
      icon: 'package-down',
      tooltip: 'DX.TABLES.TOOLTIP.ACTIONS.ARCHIVE'
    });
  }

  if (tableRegister.restoreRow) {
    actions.push({
      name: 'restore',
      action: (row, tableId, index, definition, collectionPath) =>
        new RestoreRow(tableId, row, collectionPath),
      svg: true,
      disabled: (row: IWithTableRowMeta) => row.__archive === false,
      show: (row: DxDocument) => row.__archived,
      icon: 'package-up',
      tooltip: 'DX.TABLES.TOOLTIP.ACTIONS.RESTORE'
    });
  }

  if (tableRegister.editRow) {
    actions.push({
      name: 'edit',
      action: (row, tableId, index, definition, collectionPath) =>
        new EditRow(tableId, row, collectionPath),
      svg: true,
      disabled: (row: IWithTableRowMeta) => row.__edit === false,
      show: (row, tableId, index, definition, collectionPath) =>
        definition.editRow,
      icon: 'pencil',
      tooltip: 'DX.TABLES.TOOLTIP.ACTIONS.EDIT'
    });
  }

  if (tableRegister.viewRow) {
    actions.push({
      name: 'view',
      action: (row, tableId, index, definition, collectionPath) =>
        new ViewRow(tableId, row, collectionPath),
      svg: true,
      disabled: (row: IWithTableRowMeta) => row.__view === false,
      show: (row, tableId, index, definition, collectionPath) =>
        definition.viewRow,
      icon: 'eye',
      tooltip: 'DX.TABLES.TOOLTIP.ACTIONS.VIEW'
    });
  }

  if (tableRegister.downloadRow) {
    actions.push({
      name: 'download',
      svg: true,
      action: (row, tableId, index, definition, collectionPath) =>
        new DownloadRow(tableId, row, collectionPath),
      icon: 'pdf-box',
      tooltip: 'DX.TABLES.TOOLTIP.ACTIONS.DOWNLOAD'
    });
  }

  if (tableRegister.deleteRow) {
    actions.push({
      name: 'delete',
      svg: true,
      action: (row, tableId, index, collectionPath) =>
        new DeleteRow(tableId, row, collectionPath),
      icon: 'delete',
      disabled: (row: IWithTableRowMeta) => row.__remove === false,
      show: (
        row: IWithTableRowMeta & DxDocument,
        tableId,
        index,
        definition,
        collectionPath
      ) => definition.deleteRow && (row.__archived || !definition.archiveRow)
    });
  }

  return actions;
}

export function normalizeDxTableAction<D>(
  tableId: string
): (action: ActionButtonDefinition<[D]>) => ActionButtonDefinition<[D]> {
  return function(
    action: ActionButtonDefinition<[D]>
  ): ActionButtonDefinition<[D]> {
    return {
      disabled: false,
      show: true,
      hide: false,
      color: null,
      tooltip: `TABLES.${tableId}.TOOLTIP.ACTIONS.${action.name}`,
      iconSvg: false || action['svg'],
      type: ActionButtonType.Icon,
      ...action
    };
  };
}

export function transformTableStateRegister<D>(
  tableStateRegister: Partial<DxTableState<D>>,
  tableId: string
): DxTableState<D> {
  const state: DxTableState<D> = all([
    DEFAULT_TABLE_STATE,
    {
      title: ['TABLES', tableId, 'TITLE'].join('.'),
      ...tableStateRegister,
      columns: (tableStateRegister.columns || []).map(column => ({
        ...column,
        display:
          column.display ||
          ['TABLES', tableId, 'COLUMNS', column.name].join('.'),
        color:
          typeof column.color === 'function'
            ? (column.color as ((raw: any) => string))
            : () => column.color as string
      })),
      editFormId: tableStateRegister.editFormId || tableStateRegister.formId,
      createFormId:
        tableStateRegister.createFormId || tableStateRegister.formId,
      formId:
        tableStateRegister.formId ||
        tableStateRegister.editFormId ||
        tableStateRegister.createFormId
    }
  ]) as any;
  return {
    ...state,
    actions: [
      ...getTableActionsFromRegister(state),
      ...(tableStateRegister.actions || [])
    ].map(normalizeDxTableAction(tableId))
  };
}

export function firestoreTableReducer<D>(
  state = firestoreTableInitialState,
  action: TableActions<D>
): FirestoreTableState<D> {
  if (action.type === TableActionTypes.RegisterTable) {
    state = {
      ...state,
      [action.tableId]: transformTableStateRegister(
        action.payload,
        action.tableId
      )
    };
  } else if (action.type === TableActionTypes.RegisterTables) {
    state = {
      ...state,
      ...Object.entries(action.tables)
        .map(([tableId, tableRegister]) => [
          tableId,
          all([
            transformTableStateRegister(tableRegister, tableId),
            {
              hiddenColumns: (state[tableId] || { hiddenColumns: [] })
                .hiddenColumns
            }
          ])
        ])
        .reduce(
          (acc, [tableId, tableRegister]: [string, any]) => ({
            ...acc,
            [tableId]: tableRegister
          }),
          {}
        )
    };
  } else {
    let tableState = state[action.tableId];

    if (tableState) {
      switch (action.type) {
        case TableActionTypes.SetFilter:
          tableState = {
            ...tableState,
            filtering: {
              ...tableState.filtering,
              [action.columnName]: {
                ...(tableState.filtering[action.columnName] || {}),
                value: action.filter || null
              }
            } as any
          };
          break;

        case TableActionTypes.ClearSelectedRows:
          tableState = {
            ...tableState,
            selectedRows: {}
          };
          break;

        case TableActionTypes.AddRowToSelection:
          tableState = {
            ...tableState,
            selectedRows: {
              ...tableState.selectedRows,
              [action.row.__index]: action.row
            }
          };
          break;

        case TableActionTypes.RemoveRowFromSelection:
          const selectedRows = { ...tableState.selectedRows };
          if (selectedRows.hasOwnProperty(action.row.__index)) {
            delete selectedRows[action.row.__index];
          }
          tableState = {
            ...tableState,
            selectedRows
          };
          break;

        case TableActionTypes.SetPageSize:
          tableState = {
            ...tableState,
            pagination: { ...tableState.pagination, size: action.pageSize }
          };
          break;

        case TableActionTypes.SetPage:
          tableState = {
            ...tableState,
            pagination: { ...tableState.pagination, page: action.page }
          };
          break;

        case TableActionTypes.SetCurrentTableTab:
          tableState = {
            ...tableState,
            currentTab: action.tableTab
          };
          break;

        case TableActionTypes.SetLoadingLimit:
          tableState = { ...tableState, limit: action.limit };
          break;

        case TableActionTypes.ToggleFiltering:
          tableState = {
            ...tableState,
            filtering: {
              ...tableState.filtering,
              __enabled:
                action.enabled === undefined
                  ? !tableState.filtering.__enabled
                  : action.enabled
            } as any
          };
          break;

        case TableActionTypes.TogglePagination:
          tableState = {
            ...tableState,
            pagination: {
              ...tableState.pagination,
              enabled:
                action.enabled === undefined
                  ? !tableState.pagination.enabled
                  : action.enabled
            }
          };
          break;

        case TableActionTypes.ToggleSorting:
          tableState = {
            ...tableState,
            sorting: {
              ...tableState.sorting,
              __enabled:
                action.enabled === undefined
                  ? !tableState.sorting.__enabled
                  : action.enabled
            } as any
          };
          break;

        case TableActionTypes.SetExpandedRow:
          tableState = {
            ...tableState,
            expandable: {
              ...tableState.expandable,
              row:
                action.toggle &&
                getRowId(action.row) === getRowId(tableState.expandable.row)
                  ? null
                  : action.row,
              edit: !action.editMode
                ? !tableState.viewComponent
                : action.editMode
              // full:
              //   action.rowId !== tableState.expandable.rowId
              //     ? false
              //     : tableState.expandable.full,
            }
          };
          break;

        case TableActionTypes.ClearExpandedRowId:
          tableState = {
            ...tableState,
            expandable: {
              ...tableState.expandable,
              row: null
            }
          };
          break;

        case TableActionTypes.SetExpandedRowEditMode:
          tableState = {
            ...tableState,
            expandable: {
              ...tableState.expandable,
              edit:
                action.editMode === undefined
                  ? !tableState.expandable.edit
                  : action.editMode
            }
          };
          break;

        case TableActionTypes.SetExpandedRowFullMode:
          tableState = {
            ...tableState,
            expandable: {
              ...tableState.expandable,
              full:
                action.fullMode === undefined
                  ? !tableState.expandable.full
                  : action.fullMode
            }
          };
          break;

        case TableActionTypes.ToggleDownload:
          tableState = {
            ...tableState,
            exportTableData:
              action.enabled === undefined
                ? !tableState.exportTableData
                : action.enabled
          };
          break;

        case TableActionTypes.ToggleUpload:
          tableState = {
            ...tableState,
            importTableData:
              action.enabled === undefined
                ? !tableState.importTableData
                : action.enabled
          };
          break;

        case TableActionTypes.ToggleShowArchived:
          tableState = {
            ...tableState,
            showArchived:
              action.enabled === undefined
                ? !tableState.showArchived
                : action.enabled
          };
          break;

        case TableActionTypes.ToggleShowCreatedAt:
          tableState = {
            ...tableState,
            showCreatedAt:
              action.enabled === undefined
                ? !tableState.showCreatedAt
                : action.enabled
          };
          break;

        case TableActionTypes.ToggleShowUpdatedAt:
          tableState = {
            ...tableState,
            showUpdatedAt:
              action.enabled === undefined
                ? !tableState.showUpdatedAt
                : action.enabled
          };
          break;

        case TableActionTypes.ToggleDialogView:
          tableState = {
            ...tableState,
            dialogView:
              action.enabled === undefined
                ? !tableState.dialogView
                : action.enabled,
            expandView:
              action.enabled === undefined
                ? tableState.dialogView
                : !action.enabled,
            expandable: {
              ...tableState.expandable,
              row: null
            }
          };
          break;

        case TableActionTypes.ToggleDialogEdit:
          tableState = {
            ...tableState,
            dialogEdit:
              action.enabled === undefined
                ? !tableState.dialogEdit
                : action.enabled,
            expandEdit:
              action.enabled === undefined
                ? tableState.dialogEdit
                : !action.enabled,
            expandable: {
              ...tableState.expandable,
              row: null
            }
          };
          break;

        case TableActionTypes.ToggleColumnHidden:
          tableState = {
            ...tableState,
            hiddenColumns: tableState.hiddenColumns.includes(action.columnName)
              ? tableState.hiddenColumns.filter(cn => cn !== action.columnName)
              : [...tableState.hiddenColumns, action.columnName]
          };
          break;
      }

      state = { ...state, [action.tableId]: tableState };
    }
  }

  return state;
}
